/* eslint-disable restrict-imports/restrict-imports */
import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import { Popper, TextField } from '@material-ui/core';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-pro';
import { fullPageModalLayer } from '@scale/shared/defaults';
import { ClassNameMap } from '@material-ui/styles';
import _ from 'lodash';

interface TextWrapEditComponentProps {
  params: GridRenderEditCellParams;
  updatePastActions: any;
  styles: ClassNameMap;
  taskResponseCell: boolean;
}

const TextWrapEditComponent: React.FC<TextWrapEditComponentProps> = ({
  params,
  updatePastActions,
  styles,
  taskResponseCell,
}) => {
  const apiRef = useGridApiContext();
  const [currText, setCurrText] = useState<string>(params.value ?? '');
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const [showPopper, setShowPopper] = useState<boolean>(true);
  const [changed, setChanged] = useState<boolean>(false);
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (!e.target.value.startsWith('\n')) {
      setCurrText(e.target.value);
      setChanged(true);
    }
  };
  const saveText = async (
    _e?: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
    skipSubmit?: boolean,
  ) => {
    if (changed && !skipSubmit) {
      const change = {};
      _.set(change, params.field, currText);
      updatePastActions([{ task_links: params.id, ...change }], [params.row]);
      apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: currText,
      });
      if (taskResponseCell) {
        const isDiff = _.some(
          Object.entries(params.row.oldResponseFieldsObj),
          ([field, oldResponse]) => {
            return params.field === field
              ? !_.isEqual(oldResponse, currText)
              : !_.isEqual(oldResponse, params.row[field]);
          },
        );
        const diffChanges = {};
        _.set(diffChanges, `${params.field}_diff`, {
          originalText: params.row[`${params.field}_diff`].originalText,
          newText: currText,
        });
        apiRef.current.updateRows([{ task_links: params.id, isEdited: isDiff, ...diffChanges }]);
      }
    }
    setShowPopper(false);
    await apiRef.current.commitCellChange({ id: params.id, field: params.field });
    apiRef.current.setCellMode(params.id, params.field, 'view');
  };
  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={setAnchor}
        style={{
          display: 'block',
          position: 'absolute',
          top: 0,
          height: 1,
        }}
      />
      {anchor && showPopper && params.cellMode === 'edit' && (
        <Popper
          open={anchor !== null}
          anchorEl={anchor}
          style={{
            zIndex: fullPageModalLayer + 1,
          }}
          placement="bottom-start"
        >
          <Paper elevation={1} onClick={e => e.stopPropagation()}>
            <TextField
              autoFocus
              variant="outlined"
              className={styles.textWrapItem}
              inputProps={{
                style: {
                  width:
                    currText.length > 500 && (params?.colDef?.width ?? 0) < 640
                      ? 640
                      : params?.colDef?.width ?? 420,
                  paddingBottom: 4,
                },
              }}
              multiline
              minRows={8}
              value={currText}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  saveText(undefined, true);
                  return;
                }
                if (e.key !== 'Enter') return;
                if (e.shiftKey) {
                  e.stopPropagation();
                } else {
                  saveText();
                }
              }}
              onChange={handleChange}
              onBlur={saveText}
              fullWidth
              onClick={e => e.stopPropagation()}
              FormHelperTextProps={{
                classes: {
                  root: styles.textFieldHelperText,
                },
              }}
              helperText={`Word Count: ${_.words(currText).length}`}
            />
          </Paper>
        </Popper>
      )}
    </div>
  );
};

export default TextWrapEditComponent;
